import { BUILD_STATUSES, PIPELINE_STATUSES } from "@consts";
import { extractNameFromEmail } from "@utils";
import { useDraft } from "src/queries/hooks/assistants";
import { previewDraftBuildResponseSelector } from "src/stores/selectors/previewSelectors";
import { isDraftOwnerSelector } from "src/stores/selectors/appSelectors";
import useAppStore from "src/stores/appStore";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

export const useHeaderTextContent = (isReadOnly) => {
  const isDraftOwner = useAppStore(isDraftOwnerSelector);
  const { data: draft } = useDraft();
  const displayName = draft?.userEmail ? extractNameFromEmail(draft?.userEmail) : null;
  const isFirstDraft = draft?.isFirstDraft;
  const buildDraftResponse = useAppStore(previewDraftBuildResponseSelector);

  const submittedDraft = draft?.pipelineStatus === PIPELINE_STATUSES.PUBLISHED || draft?.pipelineStatus === PIPELINE_STATUSES.FAILED;

  const getExpiryText = (expiredAt) => {
    const now = dayjs();
    const expiresAt = dayjs(expiredAt);
    const diffMs = expiresAt.diff(now);
    const duration = dayjs.duration(diffMs);
    const daysLeft = Math.ceil(duration.asDays());
    const hoursLeft = Math.ceil(duration.asHours());
    const hourText = hoursLeft === 1 ? "hour" : "hours";
    const dayText = daysLeft === 1 ? "day" : "days";

    if (hoursLeft < 1) return "1 hour";
    if (hoursLeft < 24) return `${hoursLeft} ${hourText}`;
    return `${daysLeft} ${dayText}`;
  };

  const getMainText = () => {
    if (isReadOnly) {
      if (submittedDraft) {
        return isDraftOwner
          ? `${displayName}, your changes are being processed, and will be published soon`
          : `${displayName}'s change request is being processed`;
      }

      if (draft) {
        return `This page is currently being edited by ${displayName} - Draft will expire in ${getExpiryText(draft.expiredAt)}`;
      }
    }

    if ([BUILD_STATUSES.PENDING, BUILD_STATUSES.KG_UPDATE].includes(buildDraftResponse?.status)) {
      return "Your changes are being built, we'll notify you when they're ready";
    }

    if (buildDraftResponse?.status === BUILD_STATUSES.LIVE) {
      return "Your changes have been built, and are now ready to preview";
    }

    if (isFirstDraft) {
      return `Make sure to publish your first draft, otherwise it will be discarded - less than ${getExpiryText(draft.expiredAt)} remaining`;
    }

    if (draft?.expiredAt) {
      return `Your changes have been submitted, draft will expire in ${getExpiryText(draft.expiredAt)}`;
    }

    return "Your changes have been submitted";
  };

  return {
    mainText: getMainText()
  };
};
